import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

// Hooks
import { useAuth } from '../../../providers/auth_provider';

// Utils
import { formatDate } from '../../../utils/format_date';

const ConfirmationModal = ({
    title,
    message,
    onConfirm,
    onCancel,
    isVisible,
    allAffiliates,
    selectedUser,
    onSelectAffiliate
}) => {
    if (!isVisible) return null;

    return (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content" style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', maxWidth: '700px' }}>
                    <div className="modal-header justify-content-between p-4" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                        <h5 className="modal-title">{title}</h5>
                        <button type="button" className="btn-close text-reset" onClick={onCancel} aria-label="Close" />
                    </div>
                    <div className="modal-body" style={{ padding: '1.5rem' }}>
                        <p>{message}</p>
                        <select
                            className="form-select"
                            value={selectedUser?.id || ''}
                            onChange={(e) => onSelectAffiliate(Number(e.target.value))}
                        >
                            <option value="">Select Affiliate</option>
                            {allAffiliates?.map(affiliate => (
                                <option key={affiliate.id} value={affiliate.id}>
                                    {affiliate.first_name} {affiliate.last_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="modal-footer p-4" style={{ borderTop: '1px solid #dee2e6' }}>
                        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={onConfirm}
                            disabled={!selectedUser}
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main Component
const ViewReferrals = () => {
    const { token } = useAuth();
    const { state } = useLocation();
    const showReferrals = state?.referral;

    // State variables
    const [referrals, setReferrals] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 0 });
    const [loading, setLoading] = useState(false);
    const [allAffiliates, setAllAffiliates] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [referralIds, setReferralIds] = useState([]);
    const [transfer, setTransfer] = useState(false);

    // Constants
    const LIMIT = 10;

    // Fetch referrals
    const fetchReferrals = async (page) => {
        if (!showReferrals?.referrer_id) return;

        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/affiliate/referrals/${showReferrals.referrer_id}`,
                {
                    params: { page, limit: LIMIT },
                    headers: {
                        Signature: process.env.REACT_APP_API_SIGNATURE,
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setReferrals(response.data.data);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching referrals:', error);
            toast.error('Failed to fetch referrals');
        } finally {
            setLoading(false);
        }
    };

    // Fetch all affiliate names
    const getAllAffiliateNames = async () => {
        if (!showReferrals?.referrer_id) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/transfer/affiliate`,
                { exclude_id: showReferrals.referrer_id },
                {
                    headers: {
                        Signature: process.env.REACT_APP_API_SIGNATURE,
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setAllAffiliates(response.data.data);
        } catch (error) {
            console.error('Error getting all affiliate names:', error);
            toast.error('Failed to fetch affiliates');
        } finally {
            setLoading(false);
        }
    };

    // Handle transfer of referrals
    const handleConfirmTransfer = async () => {
        if (!selectedUser || referralIds.length === 0) {
            toast.error('Please select an affiliate and referrals');
            return;
        }

        setIsModalVisible(false);
        setLoading(true);

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/admin/transfer/affiliate/refferals`,
                {
                    from_affiliate_id: showReferrals?.referrer_id,
                    to_affiliate_id: selectedUser.id,
                    referral_ids: referralIds // Send referral IDs as an array
                },
                {
                    headers: {
                        Signature: process.env.REACT_APP_API_SIGNATURE,
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            toast.success(response.data.message);
            fetchReferrals(1);
            setReferralIds([]);
            setTransfer(false);
        } catch (error) {
            console.error('Error transferring referrals:', error);
            toast.error(error.response?.data?.message || "Failed to transfer referrals");
        } finally {
            setLoading(false);
            setSelectedUser(null);
        }
    };

    // Pagination handler
    const handlePagination = (page) => {
        fetchReferrals(page);
    };

    // Select all referrals
    const handleSelectAllReferrals = (e) => {
        setReferralIds(
            e.target.checked ? referrals.map(referral => referral.id) : []
        );
    };

    // Select single referral
    const handleSelectSingleReferral = (referralId) => {
        setReferralIds(prev =>
            prev.includes(referralId)
                ? prev.filter(id => id !== referralId)
                : [...prev, referralId]
        );
    };

    // Handle selecting an affiliate in modal
    const handleSelectAffiliate = (affiliateId) => {
        const affiliate = allAffiliates.find(a => a.id === affiliateId);
        setSelectedUser(affiliate || null);
    };

    // Initial data fetch
    useEffect(() => {
        if (showReferrals) {
            fetchReferrals(1);
            getAllAffiliateNames();
        }
    }, [showReferrals]);

    return (
        <div className="card">
            {/* Header Section */}
            <div className="card-header align-items-center border-bottom">
                <div className="d-md-flex align-items-center justify-content-between gap-5">
                    <div className='d-flex gap-5 align-items-center'>
                        <button
                            className="btn btn-sm text-primary border border-primary d-flex align-items-center justify-content-center"
                            onClick={() => window.history.back()}
                        >
                            <i className="ri-arrow-left-s-line pt-1"></i> Back
                        </button>
                        <h5 className="card-title mb-0">
                            <span className='text-capitalize'>
                                {showReferrals?.referrer?.first_name} {showReferrals?.referrer?.last_name}
                            </span>'s Referrals List
                        </h5>
                    </div>
                    <div className="d-md-flex gap-2 align-items-center my-4">
                        {referralIds.length === 0 ? (
                            <button
                                className={`btn ${transfer ? 'border text-primary border-primary' : 'btn-primary '}`}
                                onClick={() => setTransfer(!transfer)}
                            >
                                Transfer Referrals
                            </button>
                        ) : (
                            <button
                                className="btn btn-success"
                                onClick={() => setIsModalVisible(true)}
                            >
                                Select Affiliate
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {
                loading ? (
                    <div className='text-center py-5'>Loading...</div>
                ) : (
                    <div>
                        {/* Referrals Table */}
                        {referrals.length === 0 ? (
                            <p>No referrals found.</p>
                        ) : (
                            <table className="table">
                                <thead>
                                    <tr>
                                        {transfer && (
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    className='form-check-input'
                                                    checked={referralIds.length === referrals.length}
                                                    onChange={handleSelectAllReferrals}
                                                />
                                            </th>
                                        )}
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Email Id</th>
                                        <th>Phone Number</th>
                                        <th>Affiliate Status</th>
                                        <th>Join On</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {referrals.map((referral, index) => (
                                        <tr key={referral.id}>
                                            {transfer && (
                                                <th>
                                                    <input
                                                      className='form-check-input'
                                                        type="checkbox"
                                                        checked={referralIds.includes(referral.id)}
                                                        onChange={() => handleSelectSingleReferral(referral.id)}
                                                    />
                                                </th>
                                            )}
                                            <td>{index + 1}</td>
                                            <td className='text-capitalize'>
                                                {referral.referrals.first_name} {referral.referrals.last_name}
                                            </td>
                                            <td>{referral.referrals.email}</td>
                                            <td>{referral.referrals.phone_no}</td>
                                            <td className='text-capitalize'>{referral.status}</td>
                                            <td className='text-capitalize'>{formatDate(referral.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <nav aria-label="Page navigation">
                                <ul className="pagination pagination-sm p-1 mt-4">
                                    <li
                                        className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}
                                        onClick={() => pagination.page > 1 && handlePagination(pagination.page - 1)}
                                    >
                                        <a className="page-link waves-effect" href="#"><i className="tf-icon ri-skip-back-mini-line ri-20px" /></a>
                                    </li>
                                    {[...Array(pagination.totalPages)].map((_, i) => (
                                        <li
                                            key={i + 1}
                                            className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}
                                            onClick={() => handlePagination(i + 1)}
                                        >
                                            <a className="page-link waves-effect" href="#">{i + 1}</a>
                                        </li>
                                    ))}
                                    <li
                                        className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}
                                        onClick={() => pagination.page < pagination.totalPages && handlePagination(pagination.page + 1)}
                                    >
                                        <a className="page-link waves-effect" href="#"><i className="tf-icon ri-skip-forward-mini-line ri-20px" /></a>
                                    </li>
                                </ul>
                            </nav>
                        )}
                    </div>
                )
            }

            {/* Confirmation Modal */}
            <ConfirmationModal
                title="Confirm Transfer"
                message={
                    <>
                        Are you sure you want to transfer these referrals to <b>{selectedUser?.first_name} {selectedUser?.last_name}</b>?
                    </>
                }
                onConfirm={handleConfirmTransfer}
                onCancel={() => {
                    setIsModalVisible(false);
                    setSelectedUser(null);
                }}
                isVisible={isModalVisible}
                allAffiliates={allAffiliates}
                selectedUser={selectedUser}
                onSelectAffiliate={handleSelectAffiliate}
            />
        </div >
    );
};

export default ViewReferrals;